import type { ReactNode } from 'react';
import { motion, type HTMLMotionProps } from 'framer-motion';
import type { LucideIcon } from 'lucide-react';

interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'children'> {
  variant?: 'primary' | 'secondary' | 'outline' | 'accent';
  size?: 'sm' | 'md' | 'lg';
  icon?: LucideIcon;
  children: ReactNode;
}

export const Button = ({
  variant = 'primary',
  size = 'md',
  icon: Icon,
  children,
  className = '',
  ...props
}: ButtonProps) => {
  const baseStyles = "inline-flex items-center justify-center rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed font-sans";
  
  const variants = {
    primary: "bg-brand-primary hover:bg-brand-primary-hover text-white shadow-lg shadow-brand-primary/20 focus:ring-brand-primary",
    secondary: "bg-brand-base hover:bg-brand-border text-white border border-brand-border focus:ring-brand-border",
    outline: "bg-transparent border-2 border-brand-border hover:border-brand-primary text-slate-300 hover:text-brand-primary focus:ring-brand-primary",
    accent: "bg-brand-accent hover:bg-brand-accent-hover text-white shadow-lg shadow-brand-accent/30 hover:shadow-brand-accent/50 hover:shadow-[0_0_20px_rgba(6,182,212,0.4)] focus:ring-brand-accent transition-all duration-300"
  };

  const sizes = {
    sm: "px-4 py-2 text-sm",
    md: "px-6 py-3 text-base",
    lg: "px-8 py-4 text-lg"
  };

  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {children}
      {Icon && <Icon className="ml-2 w-5 h-5" />}
    </motion.button>
  );
};
